/**
 * Import din exportul `.1pux` al 1Password: o arhivă zip cu `export.data`
 * (JSON: `accounts[].vaults[].items[]`) și, separat, fișierele atașate.
 *
 * Autentificările (001) și parolele (005) trec prin `mapLogin`, deci ies la
 * fel ca din CSV: `password` (+ `totp_seed`). Celelalte categorii devin
 * `generic`, cu câmpurile lor scrise ca linii „cheie: valoare" în notițe
 * (criptate) — ca la Bitwarden. Documentele (006) se sar: conținutul lor e un
 * fișier atașat, iar atașamentele nu se importă. Elementele din coș se sar;
 * cele arhivate se importă, ca la CSV. Etichetele și seifurile nu se importă.
 *
 * Arhiva se despachetează în browser; nimic din ea nu ajunge la server în clar.
 */
import JSZip from 'jszip';

import { mapLogin } from '@/features/vault/transfer/mapLogin';
import {
  isRecord,
  text,
  TransferError,
  type ParsedImport,
} from '@/features/vault/transfer/types';

const DATA_FILE = 'export.data';
/**
 * Peste atât, `export.data` nu e o listă de parole și nu-l mai parsăm. Limita
 * se verifică după despachetare (jszip nu expune public mărimea reală), deci
 * oprește doar parsarea, nu și despachetarea unui zip bomb.
 */
const MAX_DATA_BYTES = 256 * 1024 * 1024;

const CATEGORY_LOGIN = '001';
const CATEGORY_PASSWORD = '005';
const CATEGORY_DOCUMENT = '006';

function records(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function joinBlocks(...blocks: string[]): string {
  return blocks.filter((b) => b !== '').join('\n\n');
}

/** `202412` → `2024-12` (formatul `monthYear` din 1Password). */
function monthYear(value: unknown): string {
  const raw = text(value);
  return /^\d{6}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
}

/** Secunde Unix → `YYYY-MM-DD`; orice altceva rămâne text. */
function unixDate(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const date = new Date(value * 1000);
    if (!Number.isNaN(date.getTime())) return date.toISOString().slice(0, 10);
  }
  return text(value);
}

/** Valorile nevide ale unui obiect plat, pe un singur rând. */
function flatValues(value: Record<string, unknown>): string {
  return Object.values(value)
    .map(text)
    .filter((v) => v !== '')
    .join(', ');
}

interface FieldValue {
  kind: string;
  value: string;
}

/**
 * Valoarea unui câmp de secțiune. În `.1pux` e un obiect cu o singură cheie,
 * care îi dă tipul: `{string}`, `{concealed}`, `{totp}`, `{url}`, `{date}`,
 * `{monthYear}`, `{email: {email_address}}`, `{address: {...}}`,
 * `{sshKey: {privateKey}}`, `{file: {...}}` ...
 */
function fieldValue(raw: unknown): FieldValue {
  if (!isRecord(raw)) return { kind: '', value: text(raw) };
  const entry = Object.entries(raw).find(([, v]) => v !== null && v !== undefined);
  if (!entry) return { kind: '', value: '' };
  const [kind, inner] = entry;
  if (kind === 'date') return { kind, value: unixDate(inner) };
  if (kind === 'monthYear') return { kind, value: monthYear(inner) };
  // Atașamentele nu se importă; referința la fișier nu spune nimic util.
  if (kind === 'file') return { kind, value: '' };
  // Secretele rămân exact cum sunt în fișier: spațiile pot face parte din ele.
  if (kind === 'concealed' && typeof inner === 'string') return { kind, value: inner };
  if (isRecord(inner)) {
    if (kind === 'email') return { kind, value: text(inner.email_address) || flatValues(inner) };
    if (kind === 'sshKey') return { kind, value: text(inner.privateKey) };
    return { kind, value: flatValues(inner) };
  }
  return { kind, value: text(inner) };
}

interface SectionContent {
  /** Câmpurile obișnuite, ca linii „titlu: valoare". */
  lines: string[];
  /** Valorile câmpurilor TOTP, în ordinea din fișier. */
  totps: string[];
}

function readSections(sections: unknown): SectionContent {
  const content: SectionContent = { lines: [], totps: [] };
  for (const section of records(sections)) {
    for (const field of records(section.fields)) {
      const { kind, value } = fieldValue(field.value);
      if (value.trim() === '') continue;
      if (kind === 'totp') {
        content.totps.push(value.trim());
        continue;
      }
      const label = text(field.title) || text(field.id);
      content.lines.push(`${label}: ${value}`);
    }
  }
  return content;
}

interface LoginFields {
  username: string;
  password: string;
  /** Câmpurile de formular fără rol (nici username, nici parolă). */
  lines: string[];
}

function readLoginFields(fields: unknown): LoginFields {
  const result: LoginFields = { username: '', password: '', lines: [] };
  for (const field of records(fields)) {
    const value = typeof field.value === 'string' ? field.value : text(field.value);
    if (field.designation === 'username' && !result.username) {
      result.username = value.trim();
    } else if (field.designation === 'password' && !result.password) {
      // Parola rămâne exact cum e în fișier: spațiile pot face parte din ea.
      result.password = value;
    } else {
      const name = text(field.name);
      if (name && value.trim()) result.lines.push(`${name}: ${value}`);
    }
  }
  return result;
}

/** URL-urile unui element, cel principal primul, fără dubluri. */
function readUrls(overview: Record<string, unknown>): string[] {
  const all = [text(overview.url), ...records(overview.urls).map((u) => text(u.url))];
  return [...new Set(all.filter((u) => u !== ''))];
}

function isTrashed(item: Record<string, unknown>): boolean {
  if (item.trashed === true) return true;
  const state = text(item.state).toLowerCase();
  return state === 'trashed' || state === 'deleted';
}

function mapItem(item: Record<string, unknown>, result: ParsedImport): void {
  const overview = isRecord(item.overview) ? item.overview : {};
  const details = isRecord(item.details) ? item.details : {};
  const title = text(overview.title);

  if (isTrashed(item)) {
    result.skipped.push({ title, code: 'trashed' });
    return;
  }
  const category = text(item.categoryUuid);
  if (category === CATEGORY_DOCUMENT) {
    result.skipped.push({ title, code: 'unsupported_type' });
    return;
  }

  const urls = readUrls(overview);
  const login = readLoginFields(details.loginFields);
  const sections = readSections(details.sections);
  const notes = text(details.notesPlain);

  if (category === CATEGORY_LOGIN || category === CATEGORY_PASSWORD) {
    // Categoria „Parolă" își ține secretul în `details.password`.
    const password =
      login.password || (typeof details.password === 'string' ? details.password : '');
    const mapped = mapLogin({
      title,
      url: urls[0] ?? '',
      username: login.username,
      password,
      // URL-urile suplimentare, câmpurile personalizate și eventualele seed-uri
      // TOTP în plus ajung în notițe (criptate).
      notes: joinBlocks(
        notes,
        urls.slice(1).join('\n'),
        [...login.lines, ...sections.lines, ...sections.totps.slice(1).map((s) => `totp: ${s}`)].join(
          '\n',
        ),
      ),
      totp: sections.totps[0] ?? '',
    });
    result.items.push(...mapped.items);
    result.skipped.push(...mapped.skipped);
    return;
  }

  const lines = [
    ...(login.username ? [`username: ${login.username}`] : []),
    ...(login.password ? [`password: ${login.password}`] : []),
    ...login.lines,
    ...sections.lines,
    ...sections.totps.map((s) => `totp: ${s}`),
  ];
  const body = joinBlocks(lines.join('\n'), urls.join('\n'), notes);
  if (!title && !body) {
    result.skipped.push({ title, code: 'empty_entry' });
    return;
  }
  result.items.push({
    type: 'generic',
    title,
    url: null,
    username: null,
    expires_at: null,
    meta: {},
    payload: body ? { notes: body } : {},
  });
}

/** Conținutul lui `export.data`, deja despachetat. */
export function parseOnePassword1puxData(raw: string): ParsedImport {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new TransferError('invalid_file');
  }
  if (!isRecord(data) || !Array.isArray(data.accounts)) throw new TransferError('invalid_file');

  const result: ParsedImport = { items: [], skipped: [] };
  for (const account of records(data.accounts)) {
    for (const vault of records(account.vaults)) {
      for (const item of records(vault.items)) mapItem(item, result);
    }
  }
  return result;
}

export async function parseOnePassword1pux(
  archive: ArrayBuffer | Uint8Array,
): Promise<ParsedImport> {
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(archive);
  } catch {
    throw new TransferError('invalid_file');
  }
  const entry = zip.file(DATA_FILE);
  if (!entry) throw new TransferError('onepux_no_data');

  let bytes: Uint8Array;
  try {
    bytes = await entry.async('uint8array');
  } catch {
    throw new TransferError('invalid_file');
  }
  if (bytes.byteLength > MAX_DATA_BYTES) throw new TransferError('invalid_file');
  return parseOnePassword1puxData(new TextDecoder().decode(bytes));
}
